// What a file dropped on "Objects and diagrams" is stored as. The format comes
// from the file's extension: the ones the app draws keep their own shape
// (backend/services/diagrams.js ATTACH_SHAPE), anything else is kept as a file
// named by its extension. Mirrors backend/config/diagramFormats.js.

// 8 MB of file is about 10.7 MB once base64-encoded, which fits the 12 MB the
// server accepts for an upload (UPLOAD_BODY_LIMIT) with room to spare.
export const MAX_FILE_BYTES = 8 * 1024 * 1024;

// Formats the app draws, and how each travels: text as it is, bytes as base64.
const KNOWN = {
  bpmn: { format: "bpmn", label: "BPMN flow", encoding: "text" },
  svg: { format: "svg", label: "SVG image", encoding: "text" },
  png: { format: "png", label: "PNG image", encoding: "base64" },
  jpg: { format: "jpg", label: "JPG image", encoding: "base64" },
  jpeg: { format: "jpeg", label: "JPEG image", encoding: "base64" },
  gif: { format: "gif", label: "GIF image", encoding: "base64" },
  webp: { format: "webp", label: "WebP image", encoding: "base64" },
  pdf: { format: "pdf", label: "PDF", encoding: "base64" },
  mmd: { format: "mermaid", label: "Mermaid diagram", encoding: "text" },
  mermaid: { format: "mermaid", label: "Mermaid diagram", encoding: "text" },
};

// Programs, scripts and web pages: they could run on a reader's computer.
export const BLOCKED_EXTENSIONS = Object.freeze([
  "exe", "msi", "dll", "com", "scr", "bat", "cmd", "sh", "bash", "zsh", "ps1", "psm1", "vbs", "vbe", "wsf", "wsh",
  "js", "mjs", "cjs", "jse", "jar", "app", "dmg", "pkg", "deb", "rpm", "apk", "run", "bin", "elf", "so", "dylib",
  "html", "htm", "xhtml", "shtml", "mht", "mhtml", "hta", "php", "phtml", "asp", "aspx", "jsp", "cgi", "pl", "py", "rb",
  "lnk", "reg", "inf", "cpl", "msc", "gadget", "svgz",
]);
const BLOCKED_MIME = /(html|javascript|ecmascript|x-msdownload|x-msdos-program|x-executable|x-sh\b|x-csh|x-httpd|x-php|x-python|xhtml)/i;

export const formatSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round((bytes || 0) / 1024))} KB`;

export const extensionOf = (fileName) => {
  const name = String(fileName || "");
  const dot = name.lastIndexOf(".");
  return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1).toLowerCase() : "";
};

const BPMN_NAMESPACE = /omg\.org\/spec\/BPMN/i;

// { format, label, encoding, mimeType } for a file, or { error } saying why it
// can't be attached. An .xml file is a BPMN flow when it says so; otherwise
// it is kept as an XML file (sample: its first few kilobytes, as text).
export const detectFile = (file, sample = "") => {
  if (!file) return null;
  const extension = extensionOf(file.name);
  const mimeType = file.type || "";
  if (!extension) return { error: `${file.name} has no extension, so its type can't be told. Rename it, e.g. to ${file.name}.pdf.` };
  if (BLOCKED_EXTENSIONS.includes(extension) || BLOCKED_MIME.test(mimeType)) {
    return { error: `.${extension} files can't be attached: programs, scripts and web pages could run on a reader's computer. Attach a PDF or an image of it instead.` };
  }
  if (!/^[a-z0-9]{2,10}$/.test(extension)) {
    return { error: `.${extension} isn't an extension that can be stored. Use one of 2 to 10 letters or digits.` };
  }
  if (file.size > MAX_FILE_BYTES) {
    return { error: `${file.name} is ${formatSize(file.size)}. Files up to ${formatSize(MAX_FILE_BYTES)} can be attached.` };
  }
  if (file.size === 0) return { error: `${file.name} is empty.` };
  if (extension === "xml") {
    return BPMN_NAMESPACE.test(sample)
      ? { ...KNOWN.bpmn, label: "BPMN flow (XML)", mimeType }
      : { format: "xml", label: "XML file", encoding: "dataUrl", mimeType, download: true };
  }
  if (KNOWN[extension]) return { ...KNOWN[extension], mimeType };
  return { format: extension, label: `${extension.toUpperCase()} file`, encoding: "dataUrl", mimeType, download: true };
};

// The file's content as the server stores it: text, base64, or a data: URL
// for a file the app doesn't draw (it names its own type).
export const readFile = (file, encoding) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("The file couldn't be read."));
    reader.onload = () => {
      const result = String(reader.result);
      resolve(encoding === "base64" ? result.replace(/^data:[^,]*,/, "") : result);
    };
    if (encoding === "text") reader.readAsText(file);
    else reader.readAsDataURL(file);
  });

// The first few kilobytes as text, enough to recognise a BPMN file.
export const readSample = (file) =>
  new Promise((resolve) => {
    if (!file || typeof FileReader === "undefined") return resolve("");
    const reader = new FileReader();
    reader.onerror = () => resolve("");
    reader.onload = () => resolve(String(reader.result || ""));
    reader.readAsText(file.slice ? file.slice(0, 4096) : file);
  });
